import React, { useMemo, useState, Component } from 'react';
import { ComponentType } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, BarChart3Icon, TableIcon, PieChartIcon, SlidersHorizontalIcon, XIcon, BookmarkIcon } from 'lucide-react';
import { getReport, ChartType, ReportFilter } from '../data/reports';
import { getDataset } from '../data/datasets';
import { runReport } from '../utils/reportEngine';
import { ReportTable } from '../components/reports/ReportTable';
import { ReportChart } from '../components/reports/ReportChart';
import { ExportButton } from '../components/reports/ExportButton';
import { RunReportDialog } from '../components/reports/RunReportDialog';
import { useApp } from '../context/AppContext';
const viewOptions: {
  value: ChartType;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  value: 'table',
  label: 'Table',
  icon: TableIcon
}, {
  value: 'bar',
  label: 'Bar',
  icon: BarChart3Icon
}, {
  value: 'pie',
  label: 'Pie',
  icon: PieChartIcon
}];
interface RunState {
  filters?: ReportFilter[];
  presetName?: string | null;
}
export function ReportViewer() {
  const {
    reportId
  } = useParams<{
    reportId: string;
  }>();
  const location = useLocation();
  const runState = location.state as RunState | null ?? null;
  const report = reportId ? getReport(reportId) : undefined;
  const {
    presetsForReport,
    savePreset,
    deletePreset
  } = useApp();
  const [appliedFilters, setAppliedFilters] = useState<ReportFilter[]>(runState?.filters ?? []);
  const [presetName, setPresetName] = useState<string | null>(runState?.presetName ?? null);
  const [view, setView] = useState<ChartType | null>(null);
  const [runOpen, setRunOpen] = useState(false);
  const fieldLabels = useMemo(() => {
    if (!report) return new Map<string, string>();
    const ds = getDataset(report.config.datasetId);
    return new Map((ds?.fields ?? []).map(f => [f.key, f.label]));
  }, [report]);
  const result = useMemo(() => {
    if (!report) return null;
    const config = {
      ...report.config,
      filters: [...report.config.filters, ...appliedFilters]
    };
    return runReport(config);
  }, [report, appliedFilters]);
  if (!report || !result) {
    return <div className="mx-auto w-full max-w-3xl px-4 py-16 text-center" data-id="element-404">
        <p className="text-sm font-medium text-navy-900" data-id="element-405">Report not found</p>
        <Link to="/reports" className="mt-2 inline-block text-sm text-accent-700 hover:underline" data-id="element-406">
          Back to Report Library
        </Link>
      </div>;
  }
  const activeView = view ?? report.config.chartType;
  const canChart = result.grouped !== null;
  const removeFilter = (index: number) => {
    setAppliedFilters(prev => prev.filter((_, i) => i !== index));
    setPresetName(null);
  };
  return <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8" data-id="element-407">
      <Link to="/reports" className="inline-flex items-center gap-1 text-xs font-medium text-navy-400 transition-colors hover:text-navy-900" data-id="element-408">
        <ChevronLeftIcon className="h-3.5 w-3.5" data-id="element-409" /> Report Library
      </Link>

      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between" data-id="element-410">
        <div data-id="element-411">
          <h2 className="text-xl font-semibold tracking-tight text-navy-900" data-id="element-412">
            {report.name}
          </h2>
          <p className="mt-1 max-w-xl text-sm text-navy-500" data-id="element-413">
            {report.description}
          </p>
        </div>
        <ExportButton reportName={report.name} rowCount={result.rows.length} data-id="element-414" />
      </div>

      <div className="card-float mt-6 flex flex-col gap-3 rounded-2xl bg-white p-4 sm:flex-row sm:items-center sm:justify-between" data-id="element-415">
        <div className="flex min-w-0 flex-wrap items-center gap-2" data-id="element-416">
          <button type="button" onClick={() => setRunOpen(true)} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-navy-100 px-3 text-xs font-medium text-navy-700 transition-colors hover:border-navy-300 hover:text-navy-900" data-id="element-417">
            <SlidersHorizontalIcon className="h-3.5 w-3.5" data-id="element-418" />
            {appliedFilters.length > 0 ? 'Change Filters' : 'Filters'}
          </button>

          {presetName && <span className="inline-flex items-center gap-1 rounded-full bg-navy-900 px-2.5 py-1 text-[11px] font-medium text-white" data-id="element-419">
              <BookmarkIcon className="h-3 w-3 text-gold-300" data-id="element-420" />
              {presetName}
            </span>}

          {appliedFilters.length === 0 ? <span className="text-xs text-navy-300" data-id="element-421">No filters applied</span> : appliedFilters.map((f, i) => <span key={`${f.field}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-navy-50 py-1 pl-2.5 pr-1 text-[11px] font-medium text-navy-700" data-id="element-422">
                <span className="text-navy-400" data-id="element-423">
                  {fieldLabels.get(f.field) ?? f.field}
                </span>
                <span className="text-navy-300" data-id="element-424">{f.operator}</span>
                <span data-id="element-425">{f.value}</span>
                <button type="button" onClick={() => removeFilter(i)} aria-label={`Remove ${fieldLabels.get(f.field) ?? f.field} filter`} className="flex h-4 w-4 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-white hover:text-rose-600" data-id="element-426">
                  <XIcon className="h-3 w-3" data-id="element-427" />
                </button>
              </span>)}

          <span className="ml-1 shrink-0 text-xs text-navy-300" data-id="element-428">
            {result.rows.length} record{result.rows.length === 1 ? '' : 's'}
          </span>
        </div>
        {canChart && <div className="flex shrink-0 rounded-full bg-navy-50 p-0.5" role="tablist" aria-label="View type" data-id="element-429">
            {viewOptions.map(opt => <button key={opt.value} type="button" role="tab" aria-selected={activeView === opt.value} onClick={() => setView(opt.value)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${activeView === opt.value ? 'bg-navy-900 text-white' : 'text-navy-500 hover:text-navy-900'}`} data-id="element-430">
                <opt.icon className="h-3.5 w-3.5" data-id="element-431" />
                {opt.label}
              </button>)}
          </div>}
      </div>

      <motion.div key={activeView + JSON.stringify(appliedFilters)} initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      ease: 'easeOut'
    }} className="card-float mt-4 overflow-hidden rounded-2xl bg-white" data-id="element-432">
        {activeView === 'table' || !canChart ? <ReportTable columns={result.grouped && activeView === 'table' ? [{
        key: 'group',
        label: report.config.groupBy ?? 'Group'
      }, {
        key: 'value',
        label: result.metricLabel
      }] : result.columns} rows={result.grouped && activeView === 'table' ? result.grouped.map(g => ({
        group: g.group,
        value: g.value
      })) : result.rows} data-id="element-433" /> : <div className="p-4" data-id="element-434">
            <ReportChart data={result.grouped!} chartType={activeView} metricLabel={result.metricLabel} data-id="element-435" />
          </div>}
      </motion.div>

      <RunReportDialog open={runOpen} report={report} presets={presetsForReport(report.id)} initialFilters={appliedFilters} onClose={() => setRunOpen(false)} onRun={(filters, name) => {
      setAppliedFilters(filters);
      setPresetName(name);
      setRunOpen(false);
    }} savePreset={savePreset} deletePreset={deletePreset} data-id="element-436" />
    </div>;
}